import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { API_BASE_URL } from '../config';
import {
  CuzInfo,
  HatimGroup,
  MultiHatimInfo,
  MAX_CUZ_SECIMI,
  fetchCuzDurumu,
  fetchWebGroups,
  formatTarih,
  modeLabel,
  typeLabel,
  webCuzAl
} from '../lib/cuzAl';
import { bolumeKaydir } from '../lib/kaydir';
import '../assets/css/cuz-al.css';

const VARSAYILAN_ACIKLAMA = "Kur'an-ı Kerim hatim grubuna katılın ve sevap kazanın.";

const durumEtiketi = (durum: CuzInfo['durum'], secili: boolean) => {
  if (durum === 'alindi') return 'Alındı';
  if (durum === 'tamamlandi') return 'Tamamlandı';
  return secili ? 'Seçili' : 'Boş';
};

const cuzListesi = (liste: number[]) =>
  liste.map((c) => `${c}.`).join(', ') + (liste.length > 1 ? ' cüzler' : ' cüz');

// URL'de ?grup=<id> varsa o grup açık gelir (eski /cuzal/:id linkleri buraya yönlenir)
// cuz-al.css'teki liste/detay kırılımıyla aynı
const mobilMi = () => window.matchMedia('(max-width: 960px)').matches;

const urldekiGrup = () => new URLSearchParams(window.location.search).get('grup');

export default function CuzAlPanel() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<HatimGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Telefonda önce grup kartları görünür; kart seçilince cüz tablosu açılır (masaüstünde etkisiz)
  const [detay, setDetay] = useState<boolean>(() => !!urldekiGrup());
  const [cuzler, setCuzler] = useState<CuzInfo[]>([]);
  const [hatimInfo, setHatimInfo] = useState<MultiHatimInfo | null>(null);
  const [selectedHatimNo, setSelectedHatimNo] = useState<number | null>(null);
  const [cuzLoading, setCuzLoading] = useState(false);

  const [secilenler, setSecilenler] = useState<number[]>([]);
  const [uyari, setUyari] = useState<string | null>(null);

  // Bilgi formu + onay penceresi
  const [form, setForm] = useState({ ad: '', soyad: '', sehir: '' });
  const [formHata, setFormHata] = useState<string | null>(null);
  const [onayAcik, setOnayAcik] = useState(false);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [gonderimHata, setGonderimHata] = useState<string | null>(null);
  const [basariMesaji, setBasariMesaji] = useState<string | null>(null);
  const onaylaRef = useRef<HTMLButtonElement | null>(null);

  // Socket olaylarında güncel seçimi okumak için
  const selectedIdRef = useRef<string | null>(null);
  const hatimNoRef = useRef<number | null>(null);
  const socketRef = useRef<Socket | null>(null);

  const loadGroups = useCallback(async () => {
    try {
      const list = await fetchWebGroups();
      setGroups(list);
      setError(null);
      setSelectedId((prev) => {
        if (prev && list.some((g) => g._id === prev)) return prev;
        const fromUrl = urldekiGrup();
        if (fromUrl && list.some((g) => g._id === fromUrl)) return fromUrl;
        return list.length ? list[0]._id : null;
      });
    } catch (err: any) {
      setError(err?.message || 'Sunucu ile bağlantı kurulamadı. Lütfen internet bağlantınızı kontrol edin.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCuzler = useCallback(async (groupId: string, hatimNo?: number) => {
    try {
      const res = await fetchCuzDurumu(groupId, hatimNo);
      if (selectedIdRef.current !== groupId) return;
      setCuzler(res.cuzler);
      setHatimInfo(res.hatimInfo);
      if (!hatimNo) {
        const aktif = res.hatimInfo?.activeHatimNo || 1;
        hatimNoRef.current = aktif;
        setSelectedHatimNo(aktif);
      }
      // Bu arada başkası tarafından alınan cüzleri seçimden çıkar
      setSecilenler((prev) => prev.filter((n) => res.cuzler.find((c) => c.cuzNo === n)?.durum === 'bos'));
    } catch (err) {
      console.error('Cüz durumu alınamadı:', err);
    } finally {
      setCuzLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // Grup değişince cüzleri yükle
  useEffect(() => {
    selectedIdRef.current = selectedId;
    hatimNoRef.current = null;
    setSelectedHatimNo(null);
    setSecilenler([]);
    setUyari(null);
    setFormHata(null);
    setOnayAcik(false);
    setGonderimHata(null);
    setCuzler([]);
    setHatimInfo(null);
    if (selectedId) {
      setCuzLoading(true);
      loadCuzler(selectedId);
    }
  }, [selectedId, loadCuzler]);

  // Canlı güncelleme: seçili grubun odasına katıl
  useEffect(() => {
    const socket = io(API_BASE_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });
    socketRef.current = socket;

    const yenile = () => {
      const id = selectedIdRef.current;
      if (!id) return;
      loadCuzler(id, hatimNoRef.current || undefined);
      loadGroups();
    };
    socket.on('connect', () => {
      if (selectedIdRef.current) socket.emit('joinGroup', selectedIdRef.current);
    });
    socket.on('cuzTaken', yenile);
    socket.on('cuzCompleted', yenile);
    socket.on('memberJoined', yenile);
    socket.on('groupRefresh', yenile);

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [loadCuzler, loadGroups]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !selectedId) return;
    if (socket.connected) socket.emit('joinGroup', selectedId);
    return () => {
      socket.emit('leaveGroup', selectedId);
    };
  }, [selectedId]);

  const hatimSec = (hatimNo: number) => {
    if (!selectedId || hatimNo === selectedHatimNo) return;
    hatimNoRef.current = hatimNo;
    setSelectedHatimNo(hatimNo);
    setSecilenler([]);
    setUyari(null);
    setCuzLoading(true);
    loadCuzler(selectedId, hatimNo);
  };

  const cuzSec = (cuz: CuzInfo) => {
    if (cuz.durum !== 'bos') return;
    setUyari(null);
    setSecilenler((prev) => {
      if (prev.includes(cuz.cuzNo)) return prev.filter((n) => n !== cuz.cuzNo);
      if (prev.length >= MAX_CUZ_SECIMI) {
        setUyari(`En fazla ${MAX_CUZ_SECIMI} cüz seçebilirsiniz`);
        return prev;
      }
      return [...prev, cuz.cuzNo].sort((a, b) => a - b);
    });
  };

  const grupAc = (id: string) => {
    setSelectedId(id);
    setDetay(true);
    if (mobilMi()) bolumeKaydir('cuz-al');
  };

  const listeyeDon = () => {
    setDetay(false);
    if (mobilMi()) bolumeKaydir('cuz-al');
  };

  const grup = groups.find((g) => g._id === selectedId) || null;
  const toplamBos = groups.reduce((t, g) => t + (g.stats?.availableCuz || 0), 0);
  const seciliHatim = hatimInfo?.hatimler?.find((h) => h.hatimNo === selectedHatimNo);
  const bosVar = cuzler.some((c) => c.durum === 'bos');
  const asama = onayAcik ? 3 : secilenler.length ? 2 : 1;

  const cuzuAl = (e: React.FormEvent) => {
    e.preventDefault();
    if (!secilenler.length) return;
    if (!form.ad.trim() || !form.soyad.trim()) {
      setFormHata('Lütfen adınızı ve soyadınızı giriniz');
      return;
    }
    setFormHata(null);
    setGonderimHata(null);
    setOnayAcik(true);
  };

  const onayiKapat = () => {
    if (gonderiliyor || basariMesaji) return;
    setOnayAcik(false);
    setGonderimHata(null);
  };

  useEffect(() => {
    if (onayAcik) onaylaRef.current?.focus();
  }, [onayAcik]);

  // Mevcut akışla aynı: seçilen cüzler sırayla alınır, ilk hatada durulur
  const onayla = async () => {
    if (!grup || !secilenler.length) return;
    setGonderiliyor(true);
    setGonderimHata(null);
    const okumaTuru = grup.readingMode === 'rotational' ? 'donerli' : 'sabit';
    const ad = form.ad.trim();
    const soyad = form.soyad.trim();
    let hatimNo = selectedHatimNo || 1;
    let newHatimOpened = false;
    try {
      for (const cuzNo of secilenler) {
        const cevap = await webCuzAl(grup._id, { ad, soyad, cuzNo, okumaTuru, hatimNo: selectedHatimNo || 1 });
        if (!cevap.success) {
          if (cevap.newHatimOpened && cevap.newHatimNo) {
            hatimNoRef.current = cevap.newHatimNo;
            setSelectedHatimNo(cevap.newHatimNo);
            loadCuzler(grup._id, cevap.newHatimNo);
          } else {
            loadCuzler(grup._id, selectedHatimNo || undefined);
          }
          loadGroups();
          setGonderimHata(cevap.message || `${cuzNo}. cüz alınırken bir hata oluştu`);
          setGonderiliyor(false);
          return;
        }
        if (cevap.data?.hatimNo) hatimNo = cevap.data.hatimNo;
        if (cevap.data?.newHatimOpened) newHatimOpened = true;
      }
      setBasariMesaji('Cüzler başarıyla alındı! Yönlendiriliyorsunuz...');
      setTimeout(() => {
        navigate(`/cuzal/${grup._id}/basari`, {
          state: {
            hatimAdi: grup.hatimAdi,
            hatimNo,
            cuzler: secilenler,
            ad,
            soyad,
            okumaTuru,
            readingMode: grup.readingMode,
            readingType: grup.readingType,
            baslangicTarihi: grup.baslangicTarihi,
            bitisTarihi: grup.bitisTarihi,
            newHatimOpened
          }
        });
      }, 1500);
    } catch (err) {
      setGonderimHata('Sunucu ile bağlantı kurulamadı. Lütfen tekrar deneyin.');
      setGonderiliyor(false);
    }
  };

  return (
    <section id="cuz-al" className={`ht-cuzal${groups.length ? (detay ? ' is-detail' : ' is-list') : ''}`}>
      <div className="ht-wrap ht-main">
        <aside className="ht-side">
          <div className="ht-lead">
            <span className="ht-eyebrow">BOŞTA KALAN CÜZLERİ TAMAMLAYALIM</span>
            <h1>Paylaşmanın bereketine ortak olun.</h1>
            <p>Bir grup seçin, boş cüze dokunun, adınızı yazın. Hepsi bu ekranda.</p>
          </div>

          <div className="ht-list-head">
            <h2>AKTİF HATİM GRUPLARI</h2>
            {!loading && !error && groups.length > 0 && <span>{toplamBos} boş cüz</span>}
          </div>

          {loading && (
            <>
              {[60, 48, 55].map((w) => (
                <div key={w} className="ht-skitem" aria-hidden="true">
                  <div className="ht-sk" style={{ width: `${w}%`, height: 14 }} />
                  <div className="ht-sk" style={{ width: '36%', height: 10 }} />
                  <div className="ht-sk" style={{ width: '100%', height: 6 }} />
                </div>
              ))}
              <div className="ht-status" role="status">
                <SpinIcon />Gruplar yükleniyor...
              </div>
            </>
          )}

          {!loading && groups.length > 0 && (
            <ul className="ht-glist">
              {groups.map((g) => {
                const on = g._id === selectedId;
                const rozet = g.cardSettings?.badge || (g.cardSettings?.isFeatured ? 'POPÜLER' : null);
                return (
                  <li key={g._id}>
                    <button
                      type="button"
                      className={`ht-gitem${on ? ' is-on' : ''}`}
                      aria-current={on ? 'true' : undefined}
                      onClick={() => grupAc(g._id)}
                    >
                      <span className="ht-gitem-top">
                        <span className="ht-gitem-name">{g.hatimAdi}</span>
                        <span className="ht-gitem-free">{g.stats.availableCuz} boş</span>
                      </span>
                      <span className="ht-gitem-meta">
                        <span>{modeLabel(g.readingMode)}</span>
                        <span aria-hidden="true">·</span>
                        <span>{typeLabel(g.readingType)}</span>
                        {rozet && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span className="ht-badge-text">{rozet}</span>
                          </>
                        )}
                      </span>
                      <span className="ht-bar">
                        <span style={{ width: `${g.stats.completionPercentage || 0}%` }} />
                      </span>
                      <span className="ht-gitem-cta" aria-hidden="true">Katıl / Cüz Al<ArrowIcon /></span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <div className="ht-panel-col">
          <div className="ht-panel">
            {error && (
              <div className="ht-empty" role="alert">
                <h2>{error}</h2>
                <button type="button" className="ht-btn ht-btn-line" onClick={() => { setLoading(true); loadGroups(); }}>
                  Tekrar Dene
                </button>
              </div>
            )}

            {!loading && !error && groups.length === 0 && (
              <div className="ht-empty">
                <BookIcon />
                <h2>Aktif Hatim Bulunamadı</h2>
                <p>Şu anda web sitesinde yayında olan hatim grubu bulunmamaktadır.</p>
                <a href="/#download" className="ht-btn ht-btn-gold">Mobil Uygulamayı İndir</a>
              </div>
            )}

            {loading && (
              <div className="ht-status" role="status"><SpinIcon />Yükleniyor...</div>
            )}

            {grup && (
              <>
                <button type="button" className="ht-backbtn" onClick={listeyeDon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
                  Tüm Gruplar
                </button>
                <GrupBasligi grup={grup} />

                <ol className="ht-steps" aria-label="Adımlar">
                  {['Cüz seçin', 'Bilgileriniz', 'Onay'].map((label, i) => {
                    const k = i + 1;
                    const cls = k === asama ? ' is-active' : k < asama ? ' is-past' : '';
                    return (
                      <li key={label} className={`ht-step${cls}`} aria-current={k === asama ? 'step' : undefined}>
                        <span className="ht-step-dot">{k < asama ? '✓' : k}</span>{label}
                      </li>
                    );
                  })}
                </ol>

                {hatimInfo?.hasMultipleHatim && (
                  <div className="ht-htabs">
                    <span className="ht-htabs-title">
                      Bu Grupta {hatimInfo.totalHatimCount} Hatim Var · {hatimInfo.totalCompletedHatim} hatim tamamlandı
                    </span>
                    <div className="ht-htabs-row" role="group" aria-label="Hatim seçimi">
                      {hatimInfo.hatimler.map((h) => {
                        const on = h.hatimNo === selectedHatimNo;
                        return (
                          <button
                            key={h.hatimNo}
                            type="button"
                            className={`ht-htab${on ? ' is-on' : ''}`}
                            aria-pressed={on}
                            onClick={() => hatimSec(h.hatimNo)}
                          >
                            <span>
                              {h.isCompleted ? '✓ ' : ''}{h.hatimNo}. Hatim
                              {h.hatimNo === hatimInfo.activeHatimNo && !h.isCompleted && <span className="ht-htab-tag">Aktif</span>}
                            </span>
                            <small>{h.stats.available} boş</small>
                          </button>
                        );
                      })}
                    </div>
                    {seciliHatim?.isCompleted && (
                      <div className="ht-note is-warn">
                        Bu hatim tamamlanmış. Aktif hatimden cüz almak için <strong>{hatimInfo.activeHatimNo}. Hatim</strong>'i seçin.
                      </div>
                    )}
                  </div>
                )}

                {hatimInfo && !hatimInfo.hasMultipleHatim && !cuzLoading && cuzler.length > 0 && !bosVar && (
                  <div className="ht-note">
                    <span className="ht-note-title">Bu hatimin tüm cüzleri alındı!</span>
                    Yeni cüz almaya çalıştığınızda otomatik olarak yeni hatim açılacaktır.
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="ht-legend">
                    <span><i className="is-free" />Boş</span>
                    <span><i className="is-taken" />Alındı</span>
                    <span><i className="is-done" />Tamamlandı</span>
                    <span><i className="is-sel" />Seçtiğiniz</span>
                  </div>

                  {cuzLoading && cuzler.length === 0 ? (
                    <div className="ht-status" role="status"><SpinIcon />Cüzler yükleniyor...</div>
                  ) : (
                    <div className="ht-czgrid" role="group" aria-label={`${grup.hatimAdi} cüzleri`}>
                      {cuzler.map((c) => {
                        const secili = secilenler.includes(c.cuzNo);
                        const cls = c.durum === 'alindi' ? ' is-taken' : c.durum === 'tamamlandi' ? ' is-done' : secili ? ' is-sel' : '';
                        const etiket = durumEtiketi(c.durum, secili);
                        return (
                          <button
                            key={c.cuzNo}
                            type="button"
                            className={`ht-cz${cls}`}
                            disabled={c.durum !== 'bos'}
                            aria-pressed={c.durum === 'bos' ? secili : undefined}
                            aria-label={`${c.cuzNo}. cüz, ${etiket.toLowerCase()}`}
                            onClick={() => cuzSec(c)}
                          >
                            <b>{c.cuzNo}</b>
                            <small>{etiket}</small>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <form className="ht-selbox" onSubmit={cuzuAl} noValidate>
                  <div className="ht-selbox-top">
                    <div className="ht-selbox-title">
                      {secilenler.length
                        ? <>Seçtiğiniz: <span>{cuzListesi(secilenler)}</span></>
                        : 'Yukarıdan boş bir cüz seçin'}
                    </div>
                    {secilenler.length > 0 && (
                      <button type="button" className="ht-linkbtn" onClick={() => { setSecilenler([]); setUyari(null); setFormHata(null); }}>
                        Seçimi temizle
                      </button>
                    )}
                  </div>
                  {uyari && <p className="ht-alert" role="alert">{uyari}</p>}

                  {secilenler.length > 0 && (
                    <div className="ht-fields">
                      <div className="ht-field">
                        <label htmlFor="ht-ad">Adınız *</label>
                        <input
                          id="ht-ad"
                          type="text"
                          autoComplete="given-name"
                          placeholder="Adınız"
                          value={form.ad}
                          aria-invalid={!!formHata && !form.ad.trim()}
                          onChange={(e) => setForm({ ...form, ad: e.target.value })}
                        />
                      </div>
                      <div className="ht-field">
                        <label htmlFor="ht-soyad">Soyadınız *</label>
                        <input
                          id="ht-soyad"
                          type="text"
                          autoComplete="family-name"
                          placeholder="Soyadınız"
                          value={form.soyad}
                          aria-invalid={!!formHata && !form.soyad.trim()}
                          onChange={(e) => setForm({ ...form, soyad: e.target.value })}
                        />
                      </div>
                      <div className="ht-field">
                        <label htmlFor="ht-sehir">Şehir (opsiyonel)</label>
                        <input
                          id="ht-sehir"
                          type="text"
                          autoComplete="address-level2"
                          placeholder="Şehir"
                          value={form.sehir}
                          onChange={(e) => setForm({ ...form, sehir: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                  {formHata && <p className="ht-alert" role="alert">{formHata}</p>}

                  <div className="ht-selbox-bottom">
                    <span className="ht-selbox-help">
                      {secilenler.length
                        ? `${secilenler.length} / ${MAX_CUZ_SECIMI} cüz seçildi · ${modeLabel(grup.readingMode)} Hatim · ${typeLabel(grup.readingType)}${hatimInfo?.hasMultipleHatim ? ` · ${selectedHatimNo}. Hatim` : ''}`
                        : `Birden fazla cüz seçebilirsiniz (en fazla ${MAX_CUZ_SECIMI}).`}
                    </span>
                    <button type="submit" className="ht-btn ht-btn-gold" disabled={!secilenler.length}>
                      {secilenler.length > 1 ? `${secilenler.length} Cüzü Al` : 'Cüzü Al'}
                      <ArrowIcon />
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sabit menünün (z-index 999) üstünde kalsın diye body'ye taşınır */}
      {onayAcik && grup && createPortal(
        <div className="ht-overlay" onClick={onayiKapat}>
          <div
            className="ht-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ht-onay-baslik"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Escape') onayiKapat(); }}
          >
            <div className="ht-dialog-head">
              <div className="ht-dialog-cuzler">
                {secilenler.slice(0, 5).map((n) => <span key={n}>{n}</span>)}
                {secilenler.length > 5 && <span>+{secilenler.length - 5}</span>}
              </div>
              <h2 id="ht-onay-baslik">Bilgileri Onaylayın</h2>
              <p>Son adım - bilgilerinizi kontrol edin</p>
            </div>
            <dl className="ht-dl">
              <div><dt>Hatim</dt><dd>{grup.hatimAdi}</dd></div>
              <div><dt>Ad Soyad</dt><dd>{form.ad.trim()} {form.soyad.trim()}</dd></div>
              {form.sehir.trim() && <div><dt>Şehir</dt><dd>{form.sehir.trim()}</dd></div>}
              <div>
                <dt>Seçilen Cüzler ({secilenler.length} adet)</dt>
                <dd className="is-gold">{secilenler.map((n) => `${n}. Cüz`).join(', ')}</dd>
              </div>
              <div><dt>Okuma Türü</dt><dd>{modeLabel(grup.readingMode)} Hatim · {typeLabel(grup.readingType)}</dd></div>
            </dl>

            {gonderimHata && (
              <div className="ht-errbox" role="alert">
                <AlertIcon />
                <span>{gonderimHata}</span>
              </div>
            )}
            {basariMesaji && (
              <div className="ht-okbox" role="status">
                <CheckIcon />
                <span>{basariMesaji}</span>
              </div>
            )}

            <div className="ht-dialog-actions">
              <button type="button" className="ht-btn ht-btn-line" onClick={onayiKapat} disabled={gonderiliyor || !!basariMesaji}>
                Geri
              </button>
              <button
                ref={onaylaRef}
                type="button"
                className="ht-btn ht-btn-gold"
                onClick={onayla}
                disabled={gonderiliyor || !!basariMesaji}
              >
                {gonderiliyor ? <><SpinIcon dark />Cüzler alınıyor...</> : <>Onayla<CheckIcon /></>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}

function GrupBasligi({ grup }: { grup: HatimGroup }) {
  const cs = grup.cardSettings;
  const yuzde = Math.round(grup.stats.completionPercentage || 0);
  const info = grup.hatimInfo;
  return (
    <div className="ht-ghead">
      <div className="ht-ghead-info">
        <div className="ht-chips">
          {cs?.badge && (
            <span className="ht-chip ht-chip-badge" style={{ backgroundColor: cs.badgeColor || '#10B981' }}>{cs.badge}</span>
          )}
          {cs?.isFeatured && !cs?.badge && <span className="ht-chip ht-chip-gold">POPÜLER</span>}
          <span className="ht-chip ht-chip-green">{modeLabel(grup.readingMode)} Hatim</span>
          <span className="ht-chip ht-chip-gold">{typeLabel(grup.readingType)}</span>
          {info && info.totalHatimCount > 1 && (
            <span className="ht-chip ht-chip-line">
              {info.activeHatimNo}. Hatim Aktif{info.totalCompletedHatim > 0 ? ` · ${info.totalCompletedHatim} tamamlandı` : ''}
            </span>
          )}
        </div>
        <h2>{grup.hatimAdi}</h2>
        <p className="ht-ghead-desc">{cs?.shortDescription || grup.description || VARSAYILAN_ACIKLAMA}</p>
        <div className="ht-ghead-meta">
          <span>Başlangıç: <strong>{formatTarih(grup.baslangicTarihi)}</strong></span>
          <span>Periyot: <strong>{typeLabel(grup.readingType)} / {grup.stats.totalCuz} Cüz</strong></span>
          {cs?.showMemberCount !== false && <span>{grup.stats.assignedCuz} kişi katıldı</span>}
          {cs?.showLastMember !== false && grup.lastMember?.name && <span>Son katılan: {grup.lastMember.name}</span>}
        </div>
      </div>
      <div className="ht-ring-wrap">
        <div
          className="ht-ring"
          role="img"
          aria-label={`Yüzde ${yuzde} tamamlandı`}
          style={{ background: `conic-gradient(var(--ht-gold) ${yuzde * 3.6}deg, var(--ht-track) 0)` }}
        >
          <span>%{yuzde}</span>
        </div>
        <div className="ht-ring-counts">
          <span><b>{grup.stats.assignedCuz}</b> alınan</span>
          <span><b className="is-gold">{grup.stats.availableCuz}</b> boş cüz</span>
        </div>
      </div>
    </div>
  );
}

function SpinIcon({ dark = false }: { dark?: boolean }) {
  return (
    <svg className="ht-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={dark ? 'currentColor' : '#0893A5'} strokeWidth="3" strokeLinecap="round" aria-hidden="true">
      <path d="M12 3a9 9 0 1 1-9 9" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#0893A5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 6.5C10.5 5 8 4.5 4 4.5v13c4 0 6.5.5 8 2 1.5-1.5 4-2 8-2v-13c-4 0-6.5.5-8 2z" />
      <path d="M12 6.5v13" />
    </svg>
  );
}
